import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Flex } from '@driftt/dds'

import { ActionType } from 'components/ActivityActions'
import { ActionableContactListItem } from 'components/Contacts/ActionableContactListItem'
import { driftErrorStateBuilder, driftLoadingStateBuilder } from 'components/States'
import { useAccountContactActivity } from 'hooks/useAccountContactActivity'
import usePresenceSubscription from 'hooks/usePresenceSubscription'
import { AccountActivitySortValue, ContactWithActivites } from 'modules/accountActivity/domain'
import { EmptyContactsComponent } from 'pages/CCRMAccountDetailsPage/ContactsTab'
import { getCurrentOrg } from 'selectors'
import { contactPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

interface RecommendedContactsProps {
  accountId: string
  trackAction: (action: string) => void
}

const isIdentified = (contact: ContactWithActivites) => {
  return contact.endUserData?.email || contact.endUserData?.name
}

const DEFAULT_ACTION_TYPES = [
  ActionType.EMAIL,
  ActionType.LINKEDIN,
  ActionType.OUTREACH,
  ActionType.SALESLOFT
]

const RecommendedContacts: React.FC<RecommendedContactsProps> = ({ accountId, trackAction }) => {
  const { response: accountContacts, isLoading, accountContactsError } = useAccountContactActivity(
    accountId,
    5,
    1,
    AccountActivitySortValue.NUM_ACTIVITIES
  )

  const [identifiedContacts, setIdentifiedContacts] = useState<ContactWithActivites[]>([])

  useEffect(() => {
    const filtered = accountContacts?.data?.filter(isIdentified)
    if (filtered) {
      setIdentifiedContacts(filtered)
    }
  }, [accountContacts])

  const org = useSelector(getCurrentOrg)
  const contacts = accountContacts?.data?.map(contact => contact.endUserData) ?? []
  const presentEndUserIds = usePresenceSubscription(org.id, contacts)

  const LoadingComponent = driftLoadingStateBuilder('Loading contacts...')
  const ErrorComponent = driftErrorStateBuilder("this account's contacts")

  if (isLoading) {
    return <LoadingComponent />
  }

  if (accountContactsError) {
    segmentTrack(TrackType.DRAWER_ERROR, {
      error: accountContactsError,
      source: 'Recommended Contacts',
      accountId
    })
    return <ErrorComponent />
  }

  if (!isLoading && (!accountContacts || identifiedContacts.length < 1)) {
    segmentTrack(TrackType.RECOMMENDED_CONTACTS_EMPTY, { accountId })
    return <EmptyContactsComponent />
  }

  return (
    <Flex direction="column">
      {identifiedContacts.map((contact, index) => {
        return (
          <ActionableContactListItem
            key={index}
            endUserData={contact.endUserData}
            userPresence={presentEndUserIds.has(contact.endUserId) ? 'available' : 'away'}
            companyData={contact.companyData}
            onClick={() => {
              window.open(contactPath(contact.endUserId), '_blank')
              trackAction('Open contact page')
            }}
            actionTypes={
              presentEndUserIds.has(contact.endUserId)
                ? [ActionType.CHAT, ...DEFAULT_ACTION_TYPES]
                : DEFAULT_ACTION_TYPES
            }
          />
        )
      })}
    </Flex>
  )
}

export default RecommendedContacts
